import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { sendAutonomousBatch } from "./autonomousUpload";
export type { UploadBatch } from "./autonomousUpload";

type Workflow = {
  id: string; revision_id: string; status: string; message: string; stage: string;
  processed: number; total: number | null; next_attempt_at: string | null;
  files: { id: string; filename: string; url: string; sha256: string }[];
  email_status: string | null;
};

export async function workflowRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch("/api/v1" + path, {
    ...init, headers: { "x-workbench": "1", ...(init?.body instanceof FormData ? {} : { "content-type": "application/json" }), ...init?.headers },
  });
  if (!response.ok) {
    const problem = await response.json().catch(() => ({}));
    throw Object.assign(new Error(typeof problem.detail === "string" ? problem.detail : "Не удалось выполнить действие"), { status: response.status });
  }
  return response.json() as Promise<T>;
}

export function useAutonomousUploads(projectId?: string) {
  return useQuery({ queryKey: ["workflow-capabilities", projectId], queryFn: () =>
    workflowRequest<{ enabled: boolean }>("/workflow-capabilities" + (projectId ? "?project_id=" + encodeURIComponent(projectId) : "")), staleTime: 30_000 });
}

export async function uploadAutonomous(revisionId: string, files: File[], building: string) {
  return sendAutonomousBatch(revisionId, files, building, workflowRequest, sessionStorage);
}

const stages: Record<string, string> = {
  ingest: "Чтение файлов", extract: "Чтение файлов", inventory: "Состав проекта", classify: "Разделы проекта",
  vor: "Ведомость объёмов работ", parameters: "Параметры сметы",
  complete_estimate: "Заполнение сметы", comparison: "Сопоставление",
  readiness: "Полнота и арифметика",
  delivery: "Подготовка документов", queued: "В очереди", done: "Комплект готов",
};

export function AutonomousProcessing({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const queries = useQueryClient();
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const capability = useAutonomousUploads(projectId);
  const runs = useQuery({ queryKey: ["workflows", projectId],
    queryFn: () => workflowRequest<Workflow[]>("/projects/" + projectId + "/workflows"), refetchInterval: 5000 });
  const notification = useQuery({ queryKey: ["notification-settings"], queryFn: () =>
    workflowRequest<{ notification_email: string | null; email_configured: boolean }>("/notification-settings") });
  if (!capability.data?.enabled && !runs.data?.length) return null;
  async function action(runId: string, command: "pause" | "resume" | "cancel") {
    setPending(true); setError("");
    try {
      await workflowRequest("/workflows/" + runId + "/control", { method: "POST", body: JSON.stringify({ action: command }) });
      await queries.invalidateQueries({ queryKey: ["workflows", projectId] });
    } catch (cause) { setError(String(cause instanceof Error ? cause.message : cause)); }
    finally { setPending(false); }
  }
  return <section className="autonomous-processing" aria-label="Обработка проекта">
    <h2>Обработка проекта</h2>
    <p>После загрузки всего пакета подготовка продолжится на сервере. Браузер можно закрыть.</p>
    {runs.isError && <p role="alert">Не удалось прочесть статус. Данные остаются на сервере.</p>}
    {error && <p role="alert">{error}</p>}
    {runs.data?.slice(0, 3).map(run => <article key={run.id}>
      <h3>{stages[run.stage] || run.stage}</h3>
      {run.message && run.message !== run.stage && <p>{run.message}</p>}
      {run.total !== null && <p>Завершено частей этапа: {run.processed} из {run.total}. Осталось: {run.total - run.processed}.</p>}
      {run.next_attempt_at && <p>Следующая попытка: {new Date(run.next_attempt_at).toLocaleString("ru-RU")}.</p>}
      {run.files.length > 0 && <ul>{run.files.map(file =>
        <li key={file.id}><a href={file.url}>{file.filename}</a></li>)}</ul>}
      {run.email_status === "configuration_required" && <p>Файлы доступны. Для письма ещё нужна настройка почты.</p>}
      {canEdit && run.status !== "done" && <div className="actions">
        {["paused", "blocked", "cancelled"].includes(run.status)
          ? <button disabled={pending} onClick={() => action(run.id, "resume")}>Продолжить</button>
          : <button disabled={pending} onClick={() => action(run.id, "pause")}>Приостановить</button>}
        {run.status !== "cancelled" && <button disabled={pending} onClick={() => action(run.id, "cancel")}>Отменить обработку</button>}
      </div>}
    </article>)}
    {canEdit && <form onSubmit={async event => {
      event.preventDefault(); setPending(true); setError("");
      try {
        await workflowRequest("/notification-settings", { method: "PUT", body: JSON.stringify({ notification_email: email }) });
        await queries.invalidateQueries({ queryKey: ["notification-settings"] });
      } catch (cause) { setError(String(cause instanceof Error ? cause.message : cause)); }
      finally { setPending(false); }
    }}>
      <label>Почта для уведомлений
        <input type="email" value={email} placeholder={notification.data?.notification_email || "Адрес получателя"}
          onChange={event => setEmail(event.target.value)} required />
      </label>
      <button disabled={pending}>Сохранить адрес</button>
    </form>}
  </section>;
}
